<template>

  <select-input
    :label="label"
    v-model="formData"
    value-key="value"
    label-value="label"
    :placeholder="placeholder"
    :size="size"
    :list="statuses"
    :required="required"
    :disabled="disabled"
    :clearable="clearable"
    :error="error"
    @change="updated">
    <!-- XOL-67 (B8): forwards the trailing cue slot into the select's form item (like SelectApprovalStatus). -->
    <template #cue><slot name="cue"></slot></template>
  </select-input>

</template>

<script>

export default {
  name: 'select-site-status',

  props: {
    label: {
      type: String,
      default: 'Site Status'
    },
    value: {
//      type: String,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal'
    },
    placeholder: {
      type: String,
      default: 'Site Status'
    },
    helpText: {
      type: String,
      default: null
    },

    // XOL-67 (B8), additive: only these values are offered (in the config's order). Step 5 and a Candidate site's Site Details
    // offer the four handover statuses (Active, Exclusive, Land-Only, Temporary), not New, Candidate, Dismantled or Sold.
    only: {
      type: Array,
      default: null
    },

    // XOL-67 (B8), additive: a clear button. A cleared select emits null (SelectInput would say undefined).
    clearable: {
      type: Boolean,
      default: false
    },

    // XOL-67 (B8), additive: el-form-item's plain error string (Step 5: "Select the handover status."), as SelectInput's `error`.
    error: {
      type: String,
      default: null
    },

    // XOL-67 (B8), additive: the status the site was SAVED with. Candidate is offered when it is this (a Candidate site may be switched back
    // to Candidate before it is saved; the form says it cannot be set back after). Without it (undefined) the live `value` decides, as before.
    savedValue: {
      // No `type`: it is a string, null (a site with no status yet) or undefined (the caller does not say), and null / undefined must both pass.
      default: undefined
    },

    // XOL-67 (B8), additive: the accessible name of the select's inner input ("Handover status"), for a select whose label is
    // the group around it. Set on the <input> itself and kept there, as SelectOrganisation does (decision 71).
    ariaLabel: {
      type: String,
      default: null
    }
  },

  computed: {
    // XOL-67 (decision 99): a new Candidate site comes only from Complete RTA (POST /sites answers 422 use_rta). The ONLY place
    // Candidate stays is the site that already has it (its SAVED status when the caller says it, else its value): it still reads as a label. `only` narrows
    // the list further, in the config's order.
    statuses () {
      var current = this.savedValue !== undefined ? this.savedValue : this.value
      var only = this.only

      return (this.selects || []).filter(function (s) {
        if (s && s.value === 'Candidate' && current !== 'Candidate') {
          return false
        }

        return !only || !s || only.indexOf(s.value) !== -1
      })
    }
  },

  methods: {
    updated (value) {
      // SelectInput says undefined (or '') for a cleared select: a cleared handover status is null.
      var v = value === undefined || value === '' ? null : value

      this.$emit('input', v)
      this.$emit('change', v)
    },

    // The accessibility name of el-select's inner <input> (see `ariaLabel`). Set on the element itself, because el-select passes
    // no attribute through, and kept there: Element's el-input re-renders that <input> and Vue's patch removes an aria-label it
    // did not put there.
    applyInputAttrs () {
      if (!this.ariaLabel) {
        return
      }

      var input = this.$el && typeof this.$el.querySelector === 'function' ? this.$el.querySelector('input') : null

      if (!input) {
        return
      }

      this.watchInput(input)

      if (input.getAttribute('aria-label') !== this.ariaLabel) {
        input.setAttribute('aria-label', this.ariaLabel)
      }
    },

    watchInput (input) {
      var self = this

      if (this.watchedInput === input || typeof MutationObserver === 'undefined') {
        return
      }

      this.unwatchInput()
      this.watchedInput = input
      this.attrObserver = new MutationObserver(function () { self.applyInputAttrs() })
      this.attrObserver.observe(input, { attributes: true, attributeFilter: ['aria-label'] })
    },

    unwatchInput () {
      if (this.attrObserver) {
        this.attrObserver.disconnect()
      }

      this.attrObserver = null
      this.watchedInput = null
    }
  },

  mounted () {
    this.applyInputAttrs()
  },

  // The `updated` LIFECYCLE hook: a changed `ariaLabel` re-renders this component and lands here.
  updated () {
    this.applyInputAttrs()
  },

  beforeDestroy () {
    this.unwatchInput()
  },

  data () {
    return {
      formData: this.value,
      selects: this.getConfig().selects.siteStatus
    }
  }
}
</script>

<style>
.el-select {display:block;}
</style>
